import { closestCenter, DndContext } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type React from "react";
import type { FieldData } from "~/components/ui/itemModal/types";
import DragDrop from "~/images/drag_drop.svg?react";
import styles from "./index.module.scss";

interface TableComponentProps {
  data: FieldData[];
  columns: ColumnDef<FieldData>[];
  updateData: (rowIndex: number, columnId: keyof FieldData, value: string) => void;
  isEditable: boolean;
  onReorder?: (newData: FieldData[]) => void;
  tableMeta?: Record<string, unknown>;
}

const SortableItem = ({
  id,
  children,
  isEditable,
}: {
  id: string;
  children: React.ReactNode;
  isEditable: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "#f0f0f0" : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style}>
      {isEditable && (
        <td
          {...attributes}
          {...listeners}
          style={{ cursor: "grab", width: 32, textAlign: "center" }}
        >
          <DragDrop width={18} height={18} />
        </td>
      )}
      {children}
    </tr>
  );
};

const FieldTable = ({
  data,
  columns,
  updateData,
  isEditable,
  onReorder,
  tableMeta,
}: TableComponentProps) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData,
      isEditable,
      ...tableMeta,
    },
  });

  const isUrl = (value: string) => {
    try {
      new URL(value);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = data.findIndex((item) => item["id"] === active.id);
      const newIndex = data.findIndex((item) => item["id"] === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && onReorder) {
        const newData = arrayMove(data, oldIndex, newIndex);
        onReorder(newData);
      }
    }
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <table className={styles["table"]}>
        <thead>
          <tr>
            {isEditable && <th style={{ width: 32 }}></th>}
            {table.getHeaderGroups()[0]!.headers.map((header) => (
              <th key={header.id}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        </thead>
        <SortableContext
          items={data.map((row) => row["id"])}
          strategy={verticalListSortingStrategy}
        >
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => (
              <SortableItem
                key={row.original["id"]}
                id={row.original["id"]}
                isEditable={isEditable}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {cell.column.id === "delete" ||
                    cell.column.id === "settings" ||
                    cell.column.id === "フィールドコード" ||
                    cell.column.id === "フィールドタイプ" ? (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    ) : !isEditable && isUrl(cell.getValue() as string) ? (
                      <div
                        className={styles["link"]}
                        onClick={() => window.open(cell.getValue() as string, "_blank")}
                        style={{ cursor: "pointer", color: "blue" }}
                      >
                        {cell.getValue() as string}
                      </div>
                    ) : (
                      <textarea
                        rows={1}
                        value={cell.getValue() as string | number}
                        onChange={(e) =>
                          updateData(rowIndex, cell.column.id as keyof FieldData, e.target.value)
                        }
                        readOnly={!isEditable}
                      />
                    )}
                  </td>
                ))}
              </SortableItem>
            ))}
          </tbody>
        </SortableContext>
      </table>
    </DndContext>
  );
};

export default FieldTable;
